import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/app/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, MessageSquare } from "lucide-react";

export default function Enquiries() {
  const nav = useNavigate();
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const load = async () => {
      const [r1, r2] = await Promise.all([api.get("/interests/received"), api.get("/interests/sent")]);
    setReceived(r1.data.interests); setSent(r2.data.interests);
  };
  useEffect(() => { load(); }, []);

  const act = async (eid, action) => {
    try {
      const r = await api.post(`/interests/${eid}/action`, { action });
      toast.success(`Enquiry ${r.data.status}`);
      await load();
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl md:text-4xl font-medium">Enquiries</h1>
        <p className="text-stone-600 mt-1">Manage requests from both sides.</p>

        <Tabs defaultValue="received" className="mt-8">
          <TabsList>
            <TabsTrigger value="received" data-testid="tab-received">Received ({received.length})</TabsTrigger>
            <TabsTrigger value="sent" data-testid="tab-sent">Sent ({sent.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="received" className="space-y-3 mt-4">
            {received.length === 0 && <Empty msg="No enquiries received yet." />}
            {received.map((e) => (
              <div key={e.id} data-testid={`enq-recv-${e.id}`} className="p-5 bg-white border border-stone-200 rounded-2xl flex items-center gap-4">
                <div className="flex-1">
                  <div className="text-sm text-stone-500">{new Date(e.created_at).toLocaleString()}</div>
                  <div className="font-medium mt-1">{e.seeker_name} → <span className="text-stone-500">{e.listing_title}</span></div>
                  {e.message && <div className="text-sm text-stone-600 mt-1">"{e.message}"</div>}
                </div>
                <div className="flex items-center gap-2">
                  {e.status === "pending" ? (
                    <>
                      <Button data-testid={`enq-accept-${e.id}`} size="sm" className="brand-primary rounded-full" onClick={() => act(e.id, "accept")}>
                        <Check className="w-4 h-4 mr-1" /> Accept
                      </Button>
                      <Button data-testid={`enq-reject-${e.id}`} size="sm" variant="outline" className="rounded-full" onClick={() => act(e.id, "reject")}>
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className={`chip ${e.status === "accepted" ? "chip-verified" : "bg-red-50 text-red-700 border border-red-200"}`}>{e.status}</span>
                      {e.status === "accepted" && e.thread_id && (
                        <Button size="sm" variant="ghost" data-testid={`enq-chat-${e.id}`} onClick={() => nav(`/chat/${e.thread_id}`)}>
                          <MessageSquare className="w-4 h-4 mr-1" /> Chat
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="sent" className="space-y-3 mt-4">
            {sent.length === 0 && <Empty msg="You haven't sent any enquiries." />}
            {sent.map((e) => (
              <div key={e.id} data-testid={`enq-sent-${e.id}`} className="p-5 bg-white border border-stone-200 rounded-2xl flex items-center gap-4">
                <div className="flex-1">
                  <div className="text-sm text-stone-500">{new Date(e.created_at).toLocaleString()}</div>
                  <div className="font-medium mt-1">{e.listing_title}</div>
                  {e.message && <div className="text-sm text-stone-600 mt-1">"{e.message}"</div>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`chip capitalize ${e.status === "accepted" ? "chip-verified" : e.status === "rejected" ? "bg-red-50 text-red-700 border border-red-200" : ""}`}>{e.status}</span>
                  {e.status === "accepted" && e.thread_id && (
                    <Button size="sm" variant="ghost" data-testid={`enq-sent-chat-${e.id}`} onClick={() => nav(`/chat/${e.thread_id}`)}>
                      <MessageSquare className="w-4 h-4 mr-1" /> Chat
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
const Empty = ({ msg }) => <div className="text-center py-16 text-stone-500 border border-dashed border-stone-300 rounded-2xl">{msg}</div>;
